<template>
    <b-container fluid class="d-flex flex-column mh-100 h-100 no-gutters py-0 w-100 overflow-auto">
        <b-row class="w-100 no-gutters py-0">
            <MultiPeriod :ref="$id('multiPeriod')" :Configs.sync="configs" :Variant="Variant" :Errors.sync="errorList" />
        </b-row>

        <!-- Affichage des DataBlocks et du bouton d'ajout -->
        <!-- Lors ajout la valeur de la répartition correspond à celle de la première donnée -->
        <b-row class="w-100 no-gutters py-0 accordion">
            <!-- NewData -> Nouvelle Donnée --> 
            <b-button @click="checkAddData();"
                      class="mx-2 font-weight-semibold" :variant="'outline-' + Variant" pill><b-icon icon="plus-lg" scale="0.8" />{{$t('NewData')}}</b-button>
            <!-- Liste des blocs de données
                 Si pb de mise à jour de tableau dataBlocks : @see https://stackoverflow.com/questions/71153245/index-in-v-for-for-array-v-if-not-updating-vue-js -->
            <DataBlock v-for="(dataBlock, index) in dataBlocks" :key="index"
                    :Index="index + 1" :UseDistribution="useDistribution" :NbData="dataBlocks.length" :ShowOverlay="showOverlay"
                    Feature="Benchmarking"
                    :DataValue.sync="dataBlocks[index]"
                    :DisplaySettingsVisible="false"
                    :SelectedMask="SelectedMask"
                    :Variant="Variant"
                    @RemoveDataBlockEvent="removeData(index)"
                    @ChangeConfigCommonWorkDataEvent="changeConfigCommonWorkData(index)" />
            <!-- Propriétés suivantes non utilisées pour Benchmarking : :SFilterOwner.sync="dataBlock.FilterOwner" :SFilterSubscriptionHolder.sync="dataBlock.FilterSubscriptionHolder" :SFilterPayingInvoice.sync="dataBlock.FilterPayingInvoice" :SFilterObjectType.sync="dataBlock.FilterObjectType" -->
        </b-row>
	</b-container>
</template>

<script lang="ts">
    import { Component, Watch} from 'vue-property-decorator';
	import FeatureConfigWData from './FeatureConfigWData';
    import MultiPeriod from './Periods/MultiPeriod.vue';
	import DataBlock from './FeatureConfigWData/DataBlock.vue';
    import Data from "@/types/Config/Data";
    import Dictionary from '@/types/Dictionary';

    /** Traductions */
    import locales from "./locales/Benchmarking.json";
    import FeatureConfigWDataLocales from './locales/FeatureConfigWData.json';

    /** Paramétrage de la fonction Benchmarking */
	@Component({
		components: {
            DataBlock,
            MultiPeriod
		},
        i18n: { 
            messages: locales,
            sharedMessages: FeatureConfigWDataLocales
         }
    })
	export default class Benchmarking extends FeatureConfigWData<Data> {
        /** Nombre maximum de DataBlock géré par Benchmarking */
        private maxDBLength: number = 3;

        /** Indique si la répartition est utilisée ou non */
        private useDistribution: boolean = true;

        /** Mise à jour des datablocks pour appliquer la répartition "Aucune" lorsqu'il y a au moins 2 données et masquer la sélection de répartition */
        @Watch('dataBlocks', { deep: true })
        protected updateDistributions(){
            if (this.dataBlocks.length >= 2) {
                this.dataBlocks.forEach(el => el.Distribution = "None");
                this.useDistribution = false;
            }
            else
                this.useDistribution = true;
        }

        /** Permet de vérifier la validité des différents champs obligatoires */
        public CheckInvalidity(): boolean {
            let periodInvalid: boolean = false;
            let typeInvalid: boolean = false;

            // Vérification de l'invalidité de la période
            if(this.$id("multiPeriod") in this.$refs && this.$refs[this.$id("multiPeriod")] && typeof (this.$refs[this.$id("multiPeriod")] as any).CheckPeriodsInvalidity === "function")
                periodInvalid = (this.$refs[this.$id("multiPeriod")] as any).CheckPeriodsInvalidity();

            typeInvalid = this.dataBlocksInvalidity((dataBlock: Data) => {
                return dataBlock.Type == "";
            });

            console.debug("Benchmarking - CheckInvalidity - isInvalid", typeInvalid || periodInvalid);

            return typeInvalid || periodInvalid;
        }

        //#region Masque de configuration

        /** Permet de charger les périodes du masque de configuration */
        @Watch('SelectedMask')
        private loadMask() {
            if(this.$id("multiPeriod") in this.$refs && this.$refs[this.$id("multiPeriod")] && typeof (this.$refs[this.$id("multiPeriod")] as any).loadPeriods === "function")
                (this.$refs[this.$id("multiPeriod")] as any).loadPeriods(this.SelectedMask["Parameters"]);
        }

        /** Enregistrement du Mask pour Benchmarking */
        public SaveMask(): Dictionary<any> {
            let parametersValues: Dictionary<any> = {};

            parametersValues.Periods = this.configs["Periods"];
            parametersValues.Feature = this.configs["Feature"];
            parametersValues.Data = this.configs["Data"];

            return parametersValues;
        }

        //#endregion Masque de configuration

        /** Génération du titre */
        public GenerateTitle(): string {
            let title: string = this.GetDataBlocksTitle();

            if(this.$id("multiPeriod") in this.$refs && this.$refs[this.$id("multiPeriod")] && typeof (this.$refs[this.$id("multiPeriod")] as any).GeneratePeriodsTitle === "function")
                title += (this.$refs[this.$id("multiPeriod")] as any).GeneratePeriodsTitle();

            return title;
        }

        /** Permet de vérifier si on peut ajouter une nouvelle donnée si la répartition de la première donnée est égale à 'None' sinon affiche un message spécifique */
        private checkAddData(){
            let firstDistribution = this.dataBlocks.at(0).Distribution;

            if (firstDistribution == 'None')
                this.newData({ Id: this.getNewId(), Synthesis: 'Invoice', ConsumptionFlow: 'Racking', FinancialFlow: 'Debit', Type: 'FinalEnergy', NatureStatements: [], IssuerType: 'Distributor', UsePaymentDate: false, Distribution: this.dataBlocks.at(0).Distribution, Filters: [], FilterExpendables: null, FilterPurposes: null, FilterUsages: null, FilterCostDetails: null, BudgetModelId: null, FilterBudgetModel: null, FilterContracts: null, FilterProviders: null, FilterTariffs: null, FilterObjectType: null, FilterOwner: null, FilterPayingInvoice: null, FilterSubscriptionHolder: null, Correctors: null, IsAccounting: false, SlidingYear: false, WithoutEstimated: false, Factors: [], Cumulate: false, Compare: false, Dissociate: false }, this.maxDBLength)
                //this.newData({ Id: this.getNewId(), Synthesis: 'Invoice', Flow: 'Racking', Type: 'FinalEnergy', NatureStatements: [], UsePaymentDate: false, Distribution: this.dataBlocks.at(0).Distribution, Filters: [], FilterExpendables: null, FilterPurposes: null, FilterUsages: null, FilterCostDetails: null, BudgetModelId: null, FilterBudgetModel: null, FilterContracts:null, FilterProviders:null, FilterTariffs:null, FilterObjectType:null, FilterOwner:null, FilterPayingInvoice:null, FilterSubscriptionHolder: null, Correctors: null, IsAccounting: false, SlidingYear: false, WithoutEstimated: false, Factors:[], Cumulate: false, Compare: false, Dissociate: false}, this.maxDBLength)
            else
            //  distributionMessage -> "Afin d'ajouter une seconde donnée, veuillez renseigner la répartition de la Donnée 1 à « Aucune »."
            //  cannotAddData -> "Ajout de donnée impossible"
                this.$bvToast.toast(this.$t('DistributionMessage').toString(), {
                    title: this.$t('CannotAddData').toString(),
						autoHideDelay: 5000,
						variant: "warning",
						solid: true,
						appendToast: true,
                        noCloseButton: true,
						toaster: "b-toaster-top-center"
				});
        }

        mounted() {
            console.debug("Benchmarking - mounted - configs : %O - configs['Periods'] : %O ", this.configs, this.configs['Periods']);

            /** Initialisation des DataBlocks */
            this.init(
                { Id: this.getNewId(), Synthesis: this.configs["Data"][0].Synthesis, ConsumptionFlow: this.configs["Data"][0].ConsumptionFlow, FinancialFlow: this.configs["Data"][0].FinancialFlow, Type: this.configs["Data"][0].Type, NatureStatements: [], IssuerType: this.configs["Data"][0].IssuerType, UsePaymentDate: false, Distribution: this.configs["Data"][0].Distribution, Filters: [], FilterExpendables: null, FilterPurposes: null, FilterUsages: null, FilterCostDetails: null, BudgetModelId: null, FilterBudgetModel: null, FilterContracts: null, FilterProviders: null, FilterTariffs: null, FilterPayingInvoice: null, FilterOwner: null, FilterObjectType: null, FilterSubscriptionHolder: null, Correctors: null, SlidingYear: false, WithoutEstimated: false, IsAccounting: false, Factors: [], Cumulate: false, Compare: false, Dissociate: false }
                , (dataBlock) => {
                    return (dataBlock.Synthesis != '' || dataBlock.Type!== '' || dataBlock.Flow!== '' || dataBlock.FilterExpendables > 0 || dataBlock.Distribution !== '');
                 });
        }
    }
</script>

<style scoped lang="scss">
    /** Fichier .css à importer pour toutes les fonctions d'analysis (Evolution, Benchmarking, ...) */
    @import './analysis.scss';

    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est coché */
    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
            border-color: var(--Variant) !important;
            background-color: var(--Variant) !important;
        }

    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est actif */
    :deep(.custom-control-input:active ~ .custom-control-label::before) {
        background-color: var(--Variant) !important;
        border-color: var(--Variant) !important;
    }
</style>